import { useMemo } from 'react';
import { ScheduleQueries } from '@frontend/api-schedule';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { convertV3CalendarEventToV2Appointment } from '../views/CalendarV3/helpers';

interface UseGetCalendarEventInfoParams {
  eventId: string;
  locationId: string;
  enabled?: boolean;
  // passing to improve partitioned query performance
  startDate?: string;
  referenceId?: string;
}
export const useGetCalendarEventInfo = ({
  eventId,
  locationId,
  enabled,
  startDate,
  referenceId,
}: UseGetCalendarEventInfoParams) => {
  const { isScheduleV3FlagEnabled } = SchedulerV3.Helpers.useGetSchedulerV3FeatureFlagDetails();
  const { locationTimezoneMap } = SchedulerV3.Helpers.useScheduleLocationTimezoneShallowStore('locationTimezoneMap');

  const getAppointmentQuery = ScheduleQueries.useGetAppointment({
    appointmentId: eventId,
    locationId,
    isEnabled: !isScheduleV3FlagEnabled && !!eventId && !!locationId && (enabled ?? true),
  });

  const getCalendarEventsQuery = SchedulerV3.Queries.useGetCalendarEventQuery({
    request: { eventId, includeAttendeeDetails: true, startDate, referenceId },
    options: { enabled: isScheduleV3FlagEnabled && !!eventId && (enabled ?? true) },
  });

  const appointmentV3 = useMemo(() => {
    if (!getCalendarEventsQuery.data?.event) return undefined;
    return convertV3CalendarEventToV2Appointment(getCalendarEventsQuery.data.event, locationTimezoneMap);
  }, [getCalendarEventsQuery.data?.event, locationTimezoneMap]);

  return {
    eventInfo: isScheduleV3FlagEnabled ? appointmentV3 : getAppointmentQuery.data?.appointment,
    isLoading: getAppointmentQuery.isLoading || getCalendarEventsQuery.isLoading,
    isRefetching: getAppointmentQuery.isRefetching || getCalendarEventsQuery.isRefetching,
    refetch: () => {
      if (isScheduleV3FlagEnabled) {
        getCalendarEventsQuery.refetch();
      } else {
        getAppointmentQuery.refetch();
      }
    },
  };
};
